/**
 * The patient's front door (§8).
 *
 * Code only — no PIN, no password, no email (constraint 16). Branded chrome,
 * calm, and as close to nothing as a login screen can be. A patient arriving
 * here may be symptomatic; there is one field and one button.
 *
 * "Last session: X days ago" from §8 is deliberately NOT here. It cannot be —
 * until the code is entered the platform does not know who is looking at the
 * screen, and it must not: printing anything about a patient before they
 * identify themselves would make this page an oracle for anyone holding a
 * stolen code. It appears on the next screen instead, which is the first moment
 * it can honestly be shown.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api.js';
import { ACCESS_ENDED } from './copy.js';
import styles from './TherapyLogin.module.css';

/**
 * Codes are issued as `XX-0001` — two letters, a dash, four digits — which is
 * what the doctor's clinical app generates.
 *
 * The dash is inserted as the patient types, so a code can be entered with it
 * or without it and lands the same either way. Typing punctuation accurately is
 * exactly the sort of small motor task this population finds hardest, and it is
 * friction for no clinical return.
 */
function formatCode(raw: string): string {
  const clean = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  const letters = clean.slice(0, 2).replace(/[^A-Z]/g, '');
  const digits = clean.slice(letters.length).replace(/[^0-9]/g, '').slice(0, 4);
  return digits.length > 0 || clean.length > 2 ? `${letters}-${digits}` : letters;
}

export function TherapyLogin() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<{ heading: string; body: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/therapy/login', { code });
      navigate('/therapy/home', { replace: true });
    } catch (caught) {
      const status = caught instanceof ApiError ? caught.status : 0;
      if (status === 403) setError(ACCESS_ENDED);
      else if (status === 429)
        setError({
          heading: 'Too many tries',
          body: 'Please wait a few minutes and try again. If the code on your card is not working, contact Dr. Ramirez.',
        });
      else
        setError({
          heading: 'That code did not work',
          body: 'Check the card Dr. Ramirez gave you and try again.',
        });
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit}>
        <img
          src="/brand/logo-small.png"
          alt="Pathways Brain &amp; Spinal Care"
          className={styles.logo}
          width={400}
          height={150}
        />

        <h1 className={styles.heading}>Home therapy</h1>
        <p className={styles.lede}>Enter the code from your card.</p>

        <label className={styles.label} htmlFor="code">
          Your therapy code
        </label>
        <input
          id="code"
          className={styles.input}
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(formatCode(e.target.value))}
          placeholder="XX-0000"
          required
          autoFocus
        />

        {error && (
          <div className={styles.error} role="alert">
            <strong className={styles.errorHeading}>{error.heading}</strong>
            <span>{error.body}</span>
          </div>
        )}

        <button className={styles.submit} type="submit" disabled={submitting}>
          {submitting ? 'Just a moment…' : 'Start'}
        </button>

        <p className={styles.footnote}>
          This code is yours. It is not linked to your name or any of your
          details — it only opens your exercises.
        </p>
      </form>
    </main>
  );
}
